import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  IconButton,
  CircularProgress,
  TextField
} from '@mui/material';
import './post-display.css';
import { req } from './ServerUtils';
import EditIcon from '@mui/icons-material/Edit';

const PostDisplay = ({ text, setText}) => {
  const [showSeeMore, setShowSeeMore] = useState(false);
  const [showFullText, setShowFullText] = useState(false);
  const textRef = useRef(null);

  
  const getLineClamp = () => {
    return showFullText ? 100 : 3;
  };
  
  // Check if text should be truncated based on line count
  const shouldTruncate = () => {
    if (showFullText) return false;
    if (!text) return false;
    
    // Count explicit newlines
    const newlineCount = (text.match(/\n/g) || []).length;
    
    // If we already have more explicit newlines than allowed, we need to truncate
    if (newlineCount >= getLineClamp()) {
      return true;
    }
    
    // Otherwise, rely on the DOM measurement in useEffect
    return false;
  };
  
  // Check if text overflows and we need to show "see more"
  useEffect(() => {
    if (textRef.current && text) {
      const element = textRef.current;
      
      // First check for explicit newlines exceeding our limit
      if (shouldTruncate(text)) {
        setShowSeeMore(true);
        return;
      }
      
      // Then check for overflow due to word wrapping
      if (element.scrollHeight > element.clientHeight + 5) { // Add small tolerance
        setShowSeeMore(true);
      } else {
        setShowSeeMore(false);
      }
    } else {
      setShowSeeMore(false);
    }
  }, [text, showFullText]);

  return (
    <>
      <Box sx={{ position: 'relative', mb: 2, p: 2 }}>
        {!showFullText && (
            <Typography 
                ref={textRef}
                variant="body1" 
                sx={{ 
                    display: '-webkit-box',
                    WebkitLineClamp: getLineClamp(),
                    WebkitBoxOrient: 'vertical',
                    overflow: 'auto',
                    lineHeight: 1.5,
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-line' // This preserves newlines
                }}
            >
            {text || 'Your LinkedIn post will appear here'}
            </Typography>
        )}
        {showFullText && <ChangeableText text={text} setText={setText} />}
        
        {!showFullText && (
          <Box sx={{ 
            display: 'inline-block', 
            position: 'absolute',
            right: 0,
            bottom: 0,
            bgcolor: 'white',
            pr: 1,
            pl: 0.5
          }}>
            <Typography 
              variant="body2" 
              color="primary" 
              sx={{ 
                display: 'inline-block', 
                cursor: 'pointer',
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}
              onClick={() => {
                setShowSeeMore(false);
                setShowFullText(true);
              }}
            >
              ...see more
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

export default PostDisplay;



function ChangeableText({text, setText}) {
    const [buttonPos, setButtonPos] = useState(null);
    const [loading, setLoading] = useState(false);
    const editorRef = useRef(null);
    const [prompt, setPrompt] = useState('');
    const [toChange, setToChange] = useState('');

    const handleMouseUp = () => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.toString().trim() === '') {
            setButtonPos(null);
            return;
        }

        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        // Ensure it's inside the editor
        if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
            console.log(rect);
            setButtonPos({
                left: rect.x + window.scrollX - 40,
                top: rect.y + window.scrollY + 40,
            });
        } else {
            setButtonPos(null);
        }
    };

    function changeText() {

      setLoading(true);
      req('/edit-post-section', 'POST', JSON.stringify({
          post: text.replace(toChange, '<change>' + toChange + '</change>'),
          prompt_from_user: prompt
      })).then((res) => {
          if (res.new_content?.length > 0) {
              console.log(res.new_content);
              console.log("Will replace ", toChange);
              setText(text.replace(toChange, res.new_content));
              setLoading(false);
          }
      });
    }
    
    return (
        <div> 
            {loading && <CircularProgress style={{
              position: 'absolute',
              top: 0,
              left: 0,
            }} 
            />}
            <Typography variant="body1" sx={{ 
                whiteSpace: 'pre-line',
                marginTop: "5px"
            }}
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onMouseUp={handleMouseUp}
                onBlur={(e) => {
                  setText(e.target.innerText);
                }}
            >
                {text}
            </Typography>
            {buttonPos && (
                <div style={{
                    position: 'fixed',
                    left: `${buttonPos.left}px`,
                    top: `${buttonPos.top}px`,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    padding: 10,
                    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e0e0e0',
                }}>
                    <TextField 
                        style={{
                          width: 400,
                        }}
                        disabled={loading}
                        placeholder='Make this crisper...'
                        value={prompt}
                        onFocus={() => {
                          if (!loading) {
                            setToChange(window.getSelection().toString());
                          }
                        }}
                        onKeyDown={(e) => {
                          if (loading) {
                            console.log("Loading");
                            return;
                          }
                          console.log(e.key);
                          if (e.key === 'Enter') {
                            changeText();
                          }
                        }}
                        onChange={(e) => {
                            setPrompt(e.target.value);
                        }}
                    />
                </div>
            )}
        </div>
    )
}